import { logError } from '@lib/logError';
import i18n from '@translations/i18n';
import MicRecorder, { type MicRecorderInstance } from 'mic-recorder-to-mp3';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

export const MAX_RECORDING_SECONDS = 300;
export const RECORDING_WARNING_SECONDS = 15;
export const WAVEFORM_BARS = 32;

const SAMPLE_INTERVAL_MS = 80;

export type VoiceRecorderStatus = 'idle' | 'starting' | 'recording' | 'preview';

export interface VoicePreview {
  file: File;
  url: string;
  durationSeconds: number;
  peaks: number[];
}

export interface VoiceRecorder {
  status: VoiceRecorderStatus;
  elapsedSeconds: number;
  levels: number[];
  preview: VoicePreview | null;
  start: () => void;
  stop: () => void;
  discard: () => void;
  takePreview: () => VoicePreview | null;
}

const micErrorMessage = (error: unknown): string => {
  const name = error instanceof DOMException ? error.name : '';
  if (name === 'NotAllowedError' || name === 'SecurityError') {
    return String(i18n.t('chat.micDenied'));
  }
  if (name === 'NotFoundError') {
    return String(i18n.t('chat.micMissing'));
  }
  return String(i18n.t('chat.micFailed'));
};

const resamplePeaks = (samples: number[], count: number): number[] => {
  if (samples.length === 0) {
    return Array.from({ length: count }, () => 0);
  }
  return Array.from({ length: count }, (_, index) => {
    const from = Math.floor((index * samples.length) / count);
    const to = Math.max(
      from + 1,
      Math.floor(((index + 1) * samples.length) / count)
    );
    return Math.max(...samples.slice(from, to));
  });
};

const levelFromAnalyser = (
  analyser: AnalyserNode,
  buffer: Uint8Array
): number => {
  analyser.getByteTimeDomainData(buffer);
  let sum = 0;
  for (let index = 0; index < buffer.length; index += 1) {
    const centered = (buffer[index] - 128) / 128;
    sum += centered * centered;
  }
  return Math.min(1, Math.sqrt(sum / buffer.length) * 4);
};

export const useVoiceRecorder = (): VoiceRecorder => {
  const recorderRef = useRef<MicRecorderInstance | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const samplesRef = useRef<number[]>([]);
  const startedAtRef = useRef(0);
  const timerRef = useRef<number | null>(null);
  const [status, setStatus] = useState<VoiceRecorderStatus>('idle');
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [levels, setLevels] = useState<number[]>([]);
  const [preview, setPreview] = useState<VoicePreview | null>(null);

  const stopSampling = (): void => {
    if (timerRef.current != null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
    analyserRef.current?.disconnect();
    analyserRef.current = null;
  };

  const clearPreview = useCallback((current: VoicePreview | null): void => {
    if (current) URL.revokeObjectURL(current.url);
  }, []);

  const stop = useCallback((): void => {
    const recorder = recorderRef.current;
    if (!recorder || status !== 'recording') {
      return;
    }
    stopSampling();
    const durationSeconds = Math.max(
      1,
      Math.round((performance.now() - startedAtRef.current) / 1000)
    );
    const peaks = resamplePeaks(samplesRef.current, WAVEFORM_BARS);
    setStatus('starting');

    void recorder
      .stop()
      .getMp3()
      .then(([, blob]) => {
        const file = new File([blob], `voice-${Date.now()}.mp3`, {
          type: blob.type || 'audio/mpeg',
        });
        setPreview({
          file,
          url: URL.createObjectURL(file),
          durationSeconds,
          peaks,
        });
        setStatus('preview');
      })
      .catch((error: unknown) => {
        logError('voice recorder', error);
        toast.error(String(i18n.t('chat.micFailed')));
        setStatus('idle');
      });
  }, [status]);

  const start = useCallback((): void => {
    if (status !== 'idle') {
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error(String(i18n.t('chat.micUnsupported')));
      return;
    }

    const recorder = new MicRecorder({ bitRate: 128 });
    recorderRef.current = recorder;
    samplesRef.current = [];
    setLevels([]);
    setElapsedSeconds(0);
    setStatus('starting');

    // The browser asks for microphone access here, only when the user records
    void recorder
      .start()
      .then(() => {
        const { context, microphone } = recorder;
        if (context && microphone) {
          const analyser = context.createAnalyser();
          analyser.fftSize = 1024;
          microphone.connect(analyser);
          analyserRef.current = analyser;
        }
        const buffer = new Uint8Array(1024);
        startedAtRef.current = performance.now();
        timerRef.current = window.setInterval(() => {
          const analyser = analyserRef.current;
          const level = analyser ? levelFromAnalyser(analyser, buffer) : 0;
          samplesRef.current.push(level);
          setLevels(samplesRef.current.slice(-WAVEFORM_BARS));
          setElapsedSeconds(
            Math.floor((performance.now() - startedAtRef.current) / 1000)
          );
        }, SAMPLE_INTERVAL_MS);
        setStatus('recording');
      })
      .catch((error: unknown) => {
        logError('voice recorder', error);
        recorderRef.current = null;
        setStatus('idle');
        toast.error(micErrorMessage(error));
      });
  }, [status]);

  const discard = useCallback((): void => {
    stopSampling();
    if (status === 'recording') {
      try {
        recorderRef.current?.stop();
      } catch {
        // Recorder may already be idle.
      }
    }
    recorderRef.current = null;
    setPreview((current) => {
      clearPreview(current);
      return null;
    });
    setLevels([]);
    setElapsedSeconds(0);
    setStatus('idle');
  }, [clearPreview, status]);

  const takePreview = useCallback((): VoicePreview | null => {
    const current = preview;
    recorderRef.current = null;
    // The message keeps using the object URL until its upload replaces it
    setPreview(null);
    setLevels([]);
    setElapsedSeconds(0);
    setStatus('idle');
    return current;
  }, [preview]);

  // Hitting the length limit moves straight to preview
  useEffect(() => {
    if (status === 'recording' && elapsedSeconds >= MAX_RECORDING_SECONDS) {
      stop();
    }
  }, [elapsedSeconds, status, stop]);

  // Release the microphone if the chat closes mid-recording
  useEffect(
    () => () => {
      stopSampling();
      try {
        recorderRef.current?.stop();
      } catch {
        // Recorder may already be idle.
      }
    },
    []
  );

  return {
    status,
    elapsedSeconds,
    levels,
    preview,
    start,
    stop,
    discard,
    takePreview,
  };
};
